import type { Role } from '@cf/shared';
import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router';

import { RequireAuth } from './components/RequireAuth';
import { RouteError } from './components/RouteError';
import { LoginPage } from './pages/LoginPage';
import { SetupPage } from './pages/SetupPage';

/*
 * Routes are code-split per area: the owner's phone downloads the dashboard chunk only, the
 * operator tablet only the shop-floor screens. The router waits for a chunk before navigating,
 * so the current screen stays visible while the next one loads.
 */

type Mod = Record<string, unknown>;
/** Lazy page: `page(() => import('./x'), 'XPage')`. */
const page = (load: () => Promise<Mod>, name: string) => ({
  lazy: async () => ({ Component: (await load())[name] as ComponentType }),
});
/** Lazy layout behind a role guard. */
const guarded = (roles: readonly Role[], load: () => Promise<Mod>, name: string) => ({
  lazy: async () => {
    const Layout = (await load())[name] as ComponentType;
    return {
      element: (
        <RequireAuth roles={roles}>
          <Layout />
        </RequireAuth>
      ),
    };
  },
});
const guard = (roles: readonly Role[]) => (
  <RequireAuth roles={roles}>
    <Outlet />
  </RequireAuth>
);

const operator = () => import('./pages/OperatorHome');
const config = () => import('./pages/config/configPages');
const settings = () => import('./pages/config/SettingsPages');
const home = () => import('./pages/HomePage');
const production = () => import('./pages/production/WorkOrdersPage');
const docs = () => import('./pages/sales/DocumentsPages');
const customers = () => import('./pages/sales/CustomersPage');
const reports = () => import('./pages/reports/ReportsPages');
const ownerWo = () => import('./pages/owner/OwnerWorkOrderPage');
const ownerCustomers = () => import('./pages/owner/OwnerCustomerPages');
const counts = () => import('./pages/inventory/StockCountsPages');

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  { path: '/setup', element: <SetupPage />, errorElement: <RouteError /> },
  {
    path: '/help',
    errorElement: <RouteError />,
    // Every signed-in role (the guide filters itself by role).
    element: (
      <RequireAuth>
        <Outlet />
      </RequireAuth>
    ),
    children: [{ index: true, ...page(() => import('./pages/help/HelpPage'), 'HelpPage') }],
  },
  {
    path: '/operator',
    errorElement: <RouteError />,
    ...guarded(['OPERATOR', 'MANAGER'], operator, 'OperatorLayout'),
    children: [
      { index: true, ...page(operator, 'OperatorHome') },
      // Material reception is the owner's alone; the workshop makes the 3 mm wire.
      {
        path: 'receive',
        element: guard(['OWNER']),
        children: [
          { index: true, ...page(() => import('./pages/operator/ReceivePage'), 'ReceivePage') },
        ],
      },
      { path: 'wire-3mm', ...page(() => import('./pages/operator/Wire3mmPage'), 'Wire3mmPage') },
      { path: 'produce', ...page(() => import('./pages/operator/ProducePage'), 'ProducePage') },
      { path: 'pack', ...page(() => import('./pages/operator/PackPage'), 'PackPage') },
      { path: 'qc', ...page(() => import('./pages/operator/QcPage'), 'QcPage') },
      { path: 'count', ...page(() => import('./pages/operator/CountPage'), 'CountPage') },
    ],
  },
  {
    path: '/owner',
    errorElement: <RouteError />,
    element: guard(['OWNER']),
    children: [
      { index: true, ...page(() => import('./pages/OwnerHome'), 'OwnerHome') },
      { path: 'work-orders/:id', ...page(ownerWo, 'OwnerWorkOrderPage') },
      { path: 'customers', ...page(ownerCustomers, 'OwnerCustomersPage') },
      {
        path: 'orders/new',
        ...page(() => import('./pages/owner/OwnerOrderPages'), 'OwnerNewOrderPage'),
      },
      { path: 'customers/:id', ...page(ownerCustomers, 'OwnerCustomerPage') },
      {
        path: 'receive',
        ...page(() => import('./pages/owner/OwnerReceivePage'), 'OwnerReceivePage'),
      },
      {
        path: 'approvals/:id',
        ...page(() => import('./pages/owner/OwnerApprovalPages'), 'OwnerApprovalPage'),
      },
    ],
  },
  {
    path: '/',
    errorElement: <RouteError />,
    ...guarded(
      ['MANAGER', 'SALES'],
      () => import('./layouts/BackOfficeLayout'),
      'BackOfficeLayout',
    ),
    children: [
      { index: true, ...page(home, 'HomePage') },
      {
        path: 'configuration',
        ...guarded(['MANAGER'], () => import('./pages/config/ConfigLayout'), 'ConfigLayout'),
        children: [
          { index: true, element: <Navigate to="lines" replace /> },
          { path: 'lines', ...page(config, 'LinesPage') },
          { path: 'machines', ...page(config, 'MachinesPage') },
          { path: 'stages', ...page(config, 'StagesPage') },
          { path: 'scrap-reasons', ...page(config, 'ScrapReasonsPage') },
          { path: 'wire-gauges', ...page(config, 'WireGaugesPage') },
          { path: 'containers', ...page(config, 'ContainersPage') },
          { path: 'material-types', ...page(config, 'MaterialTypesPage') },
          { path: 'materials', ...page(config, 'MaterialsPage') },
          { path: 'suppliers', ...page(config, 'SuppliersPage') },
          { path: 'families', ...page(config, 'FamiliesPage') },
          { path: 'products', ...page(config, 'ProductsPage') },
          { path: 'packaging-kinds', ...page(config, 'PackagingKindsPage') },
          { path: 'packaging', ...page(config, 'PackagingPage') },
          { path: 'skus', ...page(config, 'SkusPage') },
          { path: 'company', ...page(settings, 'CompanyPage') },
          { path: 'tax', ...page(settings, 'TaxSettingsPage') },
          { path: 'payment-methods', ...page(config, 'PaymentMethodsPage') },
          { path: 'users', ...page(() => import('./pages/config/UsersPage'), 'UsersPage') },
        ],
      },
      {
        path: 'materials',
        element: guard(['MANAGER']),
        children: [
          { index: true, ...page(() => import('./pages/InventoryPage'), 'RawMaterialsPage') },
        ],
      },
      {
        path: 'inventory',
        children: [
          { index: true, ...page(() => import('./pages/InventoryPage'), 'InventoryPage') },
          {
            path: 'counts',
            element: guard(['MANAGER']),
            children: [
              { index: true, ...page(counts, 'StockCountsPage') },
              { path: ':id', ...page(counts, 'StockCountPage') },
            ],
          },
        ],
      },
      {
        path: 'production',
        element: guard(['MANAGER']),
        children: [
          { index: true, ...page(production, 'WorkOrdersPage') },
          {
            path: 'work-orders/:id',
            ...page(() => import('./pages/production/WorkOrderDetailPage'), 'WorkOrderDetailPage'),
          },
          { path: 'trace', ...page(() => import('./pages/production/TracePage'), 'TracePage') },
          { path: 'cycle', ...page(() => import('./pages/production/CyclePage'), 'CyclePage') },
          {
            path: 'orders',
            ...page(() => import('./pages/production/ClientOrdersPage'), 'ClientOrdersPage'),
          },
          // The responsable records production from the office: the same screens as the tablet.
          {
            path: 'fusion',
            ...page(() => import('./pages/operator/Wire3mmPage'), 'Wire3mmPage'),
          },
          {
            path: 'entry',
            ...page(() => import('./pages/operator/ProducePage'), 'ProducePage'),
          },
        ],
      },
      {
        path: 'sales',
        ...guarded(['SALES', 'MANAGER'], () => import('./pages/sales/common'), 'SalesLayout'),
        children: [
          { index: true, ...page(home, 'SalesPage') },
          { path: 'orders', ...page(() => import('./pages/sales/OrdersPage'), 'OrdersPage') },
          {
            path: 'orders/:id',
            ...page(() => import('./pages/sales/OrderDetailPage'), 'OrderDetailPage'),
          },
          { path: 'deliveries', ...page(docs, 'DeliveriesPage') },
          { path: 'deliveries/:id', ...page(docs, 'DeliveryDetailPage') },
          { path: 'invoices', ...page(docs, 'InvoicesPage') },
          { path: 'invoices/:id', ...page(docs, 'InvoiceDetailPage') },
          { path: 'credit-notes', ...page(docs, 'CreditNotesPage') },
          { path: 'credit-notes/:id', ...page(docs, 'CreditNoteDetailPage') },
          { path: 'proformas', ...page(docs, 'ProformasPage') },
          { path: 'customers', ...page(customers, 'CustomersPage') },
          { path: 'customers/:id/statement', ...page(customers, 'StatementPage') },
          {
            path: 'situation',
            ...page(() => import('./pages/sales/CustomerSituationPage'), 'CustomerSituationPage'),
          },
          { path: 'receivables', ...page(docs, 'ReceivablesPage') },
        ],
      },
      {
        path: 'reports',
        ...guarded(['MANAGER'], reports, 'ReportsLayout'),
        children: [
          { index: true, element: <Navigate to="copper" replace /> },
          {
            path: 'copper',
            ...page(() => import('./pages/reports/CopperReportPage'), 'CopperReportPage'),
          },
          {
            path: 'production',
            ...page(
              () => import('./pages/reports/ProductionAnalysisPage'),
              'ProductionAnalysisPage',
            ),
          },
          {
            path: 'materials',
            ...page(() => import('./pages/reports/MaterialsMonthlyPage'), 'MaterialsMonthlyPage'),
          },
          // Losses now live in the production analysis.
          { path: 'scrap', element: <Navigate to="../production" replace /> },
          // Stock valued at purchase cost: owner only (the API refuses it to others too).
          { path: 'stock', ...guarded(['OWNER'], reports, 'StockReportPage') },
          { path: 'sales', ...page(reports, 'SalesReportPage') },
          { path: 'receivables', ...page(reports, 'ReceivablesReportPage') },
        ],
      },
      {
        path: 'audit',
        element: guard(['MANAGER']),
        children: [{ index: true, ...page(() => import('./pages/AuditPage'), 'AuditPage') }],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
